import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Plus, Trash2 } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useSchools } from '@/hooks/useSchools'

interface QuizMappingDialogProps {
  quiz: any
  isOpen: boolean
  onClose: () => void
}

const QuizMappingDialog = ({ quiz, isOpen, onClose }: QuizMappingDialogProps) => {
  const [mappings, setMappings] = useState([])
  const [tasks, setTasks] = useState([])
  const [resources, setResources] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()
  const { schools } = useSchools()

  const [newMapping, setNewMapping] = useState({
    answer_pattern: '',
    task_ids: [],
    resource_ids: [],
    event_ids: [],
    school_id: ''
  })

  useEffect(() => {
    if (quiz && isOpen) {
      fetchMappings()
      fetchTasksResourcesAndEvents()
    }
  }, [quiz, isOpen])

  useEffect(() => {
    if (isOpen) {
      // Set up real-time subscription for mappings
      const mappingsChannel = supabase
        .channel('quiz-mappings-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'quiz_answer_mappings'
          },
          () => {
            fetchMappings()
          }
        )
        .subscribe()

      return () => {
        supabase.removeChannel(mappingsChannel)
      }
    }
  }, [isOpen])

  const fetchMappings = async () => {
    if (!quiz) return

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('quiz_answer_mappings')
        .select(`
          *,
          schools (
            id,
            name
          )
        `)
        .eq('quiz_id', quiz.id)
        .order('answer_pattern')

      if (error) throw error
      setMappings(data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching mappings",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchTasksResourcesAndEvents = async () => {
    try {
      // Get quiz's school_id to filter tasks/resources
      const quizSchoolId = quiz?.school_id || null

      // Fetch inspiration type tasks - global + school-specific (if quiz has a school)
      const tasksQueries = [
        supabase
          .from('admin_tasks')
          .select('id, title, domain')
          .eq('type', 'inspiration')
          .is('school_id', null)
          .order('title')
      ]

      if (quizSchoolId) {
        tasksQueries.push(
          supabase
            .from('admin_tasks')
            .select('id, title, domain')
            .eq('type', 'inspiration')
            .eq('school_id', quizSchoolId)
            .order('title')
        )
      }

      // Fetch inspiration type resources - global + school-specific (if quiz has a school)
      const resourcesQueries = [
        supabase
          .from('admin_resources')
          .select('id, title, domain')
          .eq('type', 'inspiration')
          .is('school_id', null)
          .order('title')
      ]

      if (quizSchoolId) {
        resourcesQueries.push(
          supabase
            .from('admin_resources')
            .select('id, title, domain')
            .eq('type', 'inspiration')
            .eq('school_id', quizSchoolId)
            .order('title')
        )
      }

      const [tasksResponses, resourcesResponses, eventsResponse] = await Promise.all([
        Promise.all(tasksQueries),
        Promise.all(resourcesQueries),
        supabase
          .from('events')
          .select('id, title, domain, type')
          .order('title')
      ])

      // Combine global and school-specific tasks
      const allTasks = tasksResponses.flatMap(response => response.data || [])
      const allResources = resourcesResponses.flatMap(response => response.data || [])

      if (tasksResponses.some(r => r.error)) throw tasksResponses.find(r => r.error)?.error
      if (resourcesResponses.some(r => r.error)) throw resourcesResponses.find(r => r.error)?.error
      if (eventsResponse.error) throw eventsResponse.error

      setTasks(allTasks)
      setResources(allResources)
      setEvents(eventsResponse.data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching tasks, resources, and events",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleAddMapping = async () => {
    if (!quiz || !newMapping.answer_pattern) return

    try {
      const mappingData = {
        quiz_id: quiz.id,
        answer_pattern: newMapping.answer_pattern,
        task_ids: newMapping.task_ids,
        resource_ids: newMapping.resource_ids,
        event_ids: newMapping.event_ids,
        school_id: newMapping.school_id === 'global' ? null : newMapping.school_id || null
      }

      const { error } = await supabase
        .from('quiz_answer_mappings')
        .insert(mappingData)

      if (error) throw error

      toast({ title: "Mapping added successfully" })
      setNewMapping({
        answer_pattern: '',
        task_ids: [],
        resource_ids: [],
        event_ids: [],
        school_id: ''
      })
      fetchMappings()
    } catch (error: any) {
      toast({
        title: "Error adding mapping",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleDeleteMapping = async (mappingId: string) => {
    try {
      const { error } = await supabase
        .from('quiz_answer_mappings')
        .delete()
        .eq('id', mappingId)

      if (error) throw error

      toast({ title: "Mapping deleted successfully" })
      fetchMappings()
    } catch (error: any) {
      toast({
        title: "Error deleting mapping",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleTaskSelection = (taskId: string) => {
    setNewMapping(prev => ({
      ...prev,
      task_ids: prev.task_ids.includes(taskId)
        ? prev.task_ids.filter(id => id !== taskId)
        : [...prev.task_ids, taskId]
    }))
  }

  const handleResourceSelection = (resourceId: string) => {
    setNewMapping(prev => ({
      ...prev,
      resource_ids: prev.resource_ids.includes(resourceId)
        ? prev.resource_ids.filter(id => id !== resourceId)
        : [...prev.resource_ids, resourceId]
    }))
  }

  const handleEventSelection = (eventId: string) => {
    setNewMapping(prev => ({
      ...prev,
      event_ids: prev.event_ids.includes(eventId)
        ? prev.event_ids.filter(id => id !== eventId)
        : [...prev.event_ids, eventId]
    }))
  }

  if (!quiz) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Answer Mappings for {quiz.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add New Mapping */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-semibold">Add New Answer Pattern Mapping</h3>
            <p className="text-sm text-muted-foreground">
              Map answer patterns (e.g., AAA, ABC) to recommended inspiration tasks, resources, and events
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pattern">Answer Pattern (e.g., AAA, ABC)</Label>
                <Input
                  id="pattern"
                  value={newMapping.answer_pattern}
                  onChange={(e) => setNewMapping({ ...newMapping, answer_pattern: e.target.value.toUpperCase() })}
                  placeholder="AAA"
                  maxLength={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="school_id">Target University</Label>
                <Select value={newMapping.school_id} onValueChange={(value) => setNewMapping({ ...newMapping, school_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target university" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (All Universities)</SelectItem>
                    {schools.map(school => (
                      <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {quiz?.school_id ? 'Quiz is school-specific. You can create global or school-specific mappings.' : 'Quiz is global. You can create global or school-specific mappings.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Select Inspiration Tasks</Label>
                <Select onValueChange={handleTaskSelection}>
                  <SelectTrigger>
                    <SelectValue placeholder="Add tasks" />
                  </SelectTrigger>
                  <SelectContent>
                    {tasks.map(task => (
                      <SelectItem key={task.id} value={task.id}>
                        {task.title} ({task.domain})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-1">
                  {newMapping.task_ids.map(taskId => {
                    const task = tasks.find(t => t.id === taskId)
                    return task ? (
                      <Badge key={taskId} variant="secondary" className="text-xs">
                        {task.title}
                        <button
                          onClick={() => handleTaskSelection(taskId)}
                          className="ml-1 text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ) : null
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Select Inspiration Resources</Label>
                <Select onValueChange={handleResourceSelection}>
                  <SelectTrigger>
                    <SelectValue placeholder="Add resources" />
                  </SelectTrigger>
                  <SelectContent>
                    {resources.map(resource => (
                      <SelectItem key={resource.id} value={resource.id}>
                        {resource.title} ({resource.domain})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-1">
                  {newMapping.resource_ids.map(resourceId => {
                    const resource = resources.find(r => r.id === resourceId)
                    return resource ? (
                      <Badge key={resourceId} variant="secondary" className="text-xs">
                        {resource.title}
                        <button
                          onClick={() => handleResourceSelection(resourceId)}
                          className="ml-1 text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ) : null
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Select Events</Label>
                <Select onValueChange={handleEventSelection}>
                  <SelectTrigger>
                    <SelectValue placeholder="Add events" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map(event => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title} ({event.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-1">
                  {newMapping.event_ids.map(eventId => {
                    const event = events.find(e => e.id === eventId)
                    return event ? (
                      <Badge key={eventId} variant="secondary" className="text-xs">
                        {event.title}
                        <button
                          onClick={() => handleEventSelection(eventId)}
                          className="ml-1 text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ) : null
                  })}
                </div>
              </div>
            </div>

            <Button onClick={handleAddMapping} disabled={!newMapping.answer_pattern}>
              <Plus className="w-4 h-4 mr-2" />
              Add Mapping
            </Button>
          </div>

          {/* Existing Mappings */}
          <div className="space-y-4">
            <h3 className="font-semibold">Existing Mappings</h3>
            {loading ? (
              <p>Loading mappings...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Answer Pattern</TableHead>
                    <TableHead>University</TableHead>
                    <TableHead>Tasks</TableHead>
                    <TableHead>Resources</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.map((mapping: any) => (
                    <TableRow key={mapping.id}>
                      <TableCell>
                        <Badge>{mapping.answer_pattern}</Badge>
                      </TableCell>
                      <TableCell>
                        {mapping.school_id ? (
                          <Badge variant="outline">{mapping.schools?.name || 'Unknown'}</Badge>
                        ) : (
                          <Badge variant="secondary">Global</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {mapping.task_ids?.map((taskId: string) => {
                            const task = tasks.find(t => t.id === taskId)
                            return task ? (
                              <Badge key={taskId} variant="outline" className="text-xs">
                                {task.title}
                              </Badge>
                            ) : null
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {mapping.resource_ids?.map((resourceId: string) => {
                            const resource = resources.find(r => r.id === resourceId)
                            return resource ? (
                              <Badge key={resourceId} variant="outline" className="text-xs">
                                {resource.title}
                              </Badge>
                            ) : null
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {mapping.event_ids?.map((eventId: string) => {
                            const event = events.find(e => e.id === eventId)
                            return event ? (
                              <Badge key={eventId} variant="outline" className="text-xs">
                                {event.title}
                              </Badge>
                            ) : null
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteMapping(mapping.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {mappings.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">
                        No mappings configured yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default QuizMappingDialog
